import {
  BehaviorSubject,
  Observable,
  throwError as observableThrowError,
} from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  publishReplay,
  refCount,
  tap,
} from 'rxjs/operators';
import * as URI from 'urijs';
import { v4 as uuid } from 'uuid';

import { AppConfig } from '®shared/config';
import {
  UserPermissions,
  createDefaultUserPermissions,
  createUserPermissions,
} from '®shared/permissions';
import { RedirectionService } from '®shared/redirection';

import { AuthenticationFailedException } from './authentication-failed-exception';
import { AuthorizationFailedException } from './authorization-failed-exception';
import { User } from './user';
import { UserScopesService } from './user-scopes.service';

export type AuthorizationHeaderJson = { authorization: string };
// eslint-disable-next-line @typescript-eslint/naming-convention
export type EffectiveUserHeaderJson = { 'X-Effective-User': string };

export type AuthPredicate<U extends User> = (user: U) => boolean;

export abstract class AuthProvider<UserAtProvider extends User> {
  private authorizationFailedRedirectUrl: string;

  private currentUser$: Observable<UserAtProvider>;
  public currentUser: UserAtProvider;
  public sessionId: string = uuid();

  private userSubject = new BehaviorSubject<UserAtProvider>(undefined);

  private isLockedForRefresh = false;

  constructor(
    public appConfig: AppConfig,
    public redirectionService: RedirectionService,
    public userScopesService: UserScopesService,
  ) {
    this.authorizationFailedRedirectUrl = appConfig.betaUrl();
  }

  get user$(): Observable<UserAtProvider> {
    return this.userSubject
      .asObservable()
      .pipe(filter(user => (user ? true : false)));
  }

  get permissions$(): Observable<UserPermissions> {
    return this.user$.pipe(
      map(user => this.getUserPermissionsOrDefaults(user)),
    );
  }

  /**
   * For an optional user, compute their permissions, or return default permissions.
   * @param  {User}            user An optional user
   * @return {UserPermissions}      The user's permissions, or a default set of permissions
   */
  getUserPermissionsOrDefaults(user?: User): UserPermissions {
    return user
      ? createUserPermissions(this.appConfig, user.scopes)
      : createDefaultUserPermissions(this.appConfig.tenant.roleCodesByRoleId);
  }

  /**
   * Obtain a new token for the user - typically redirecting through an IDP and returning.
   *
   * @param  {?string}                    returnUrl An optional return URL
   * @return {Observable<UserAtProvider>}           the user, once authenticated
   */
  abstract obtainNewToken(returnUrl?: string): Observable<UserAtProvider>;

  /**
   * Authenticate a user with the current identity provider.
   *
   * Override this to specify how to establish the User's credentials.
   *
   * @return {Observable<UserAtProvider>} the user, if they are authenticated
   */
  abstract getCurrentToken(returnUrl?: string): Observable<UserAtProvider>;

  /**
   * Refresh the current OAuth access token.
   *
   * @return {Observable<UserAtProvider>} the user profile, once authenticated
   */
  abstract refreshToken(): Observable<UserAtProvider>;

  /**
   * Update the local state after the user is changed.
   *
   * @param {UserAtProvider|null} user The current user
   */
  updateUser(user: UserAtProvider | null): void {
    this.userSubject.next(user);
    this.currentUser = user;
    this.sessionId = uuid();
  }

  /**
   * Logout, which must destroy the local user record.
   */
  logout(): void {
    this.updateUser(undefined);
    this.currentUser$ = undefined;
    this.isLockedForRefresh = false;
  }

  /**
   * Authenticate a user with the current identity provider.
   *
   * @return {Observable<UserAtProvider>} the user, if they are authenticated
   */
  authenticate(returnUrl?: string): Observable<UserAtProvider> {
    if (!this.currentUser$) {
      this.currentUser$ =
        // fetch current token
        this.getCurrentToken(returnUrl).pipe(
          // if that fails, attempt to refresh token
          catchError(error => this.onGetCurrentTokenFailed(error)),
          // if that fails, attempt to obtain a new token
          catchError(error => this.onTokenRefreshFailed(error, returnUrl)),
          tap(user => this.updateUser(user)),
          // used in more than one downstream observable, so multicasted with publishReplay/refCount
          publishReplay(1),
          refCount(),
        );
    }

    return this.currentUser$;
  }

  /**
   * Reauthenticate the user (unless currently in the process of reauthenticating)
   *
   * @return {Observable<UserAtProvider>} the user profile, once authenticated
   */
  reauthenticate(returnUrl?: string): Observable<UserAtProvider> {
    if (!this.isLockedForRefresh) {
      this.isLockedForRefresh = true;
      this.currentUser$ =
        // attempt a token refresh
        this.refreshToken().pipe(
          // if that fails, attempt to obtain a new token
          catchError(error => this.onTokenRefreshFailed(error, returnUrl)),
          tap(user => {
            this.updateUser(user);
            this.isLockedForRefresh = false;
          }),
          finalize(() => {
            this.isLockedForRefresh = false;
          }),
          // used in more than one downstream observable, so multicasted with publishReplay/refCount
          publishReplay(1),
          refCount(),
        );
    }

    return this.currentUser$;
  }

  /**
   * When attempting to get the current token, we may encounter authentication-failed
   * errors. In these cases, attempt token-refresh.
   *
   * @param  {any}                        error an error
   * @return {Observable<UserAtProvider>}       the user, once authenticated
   * @throws {any}                              when token-refresh fails
   */
  onGetCurrentTokenFailed(error: any): Observable<UserAtProvider> {
    if (error instanceof AuthenticationFailedException) {
      return this.refreshToken();
    }
    throw error;
  }

  /**
   * When attempting to refresh the current token, we may encounter authentication-failed
   * errors. In these cases, obtain a new token.
   *
   * @param  {any}                        error     an error
   * @param  {string}                     returnUrl an optional return URL
   * @return {Observable<UserAtProvider>}           the user, once authenticated
   * @throws {any}                                  when obtain-token fails
   */
  onTokenRefreshFailed(
    error: any,
    returnUrl?: string,
  ): Observable<UserAtProvider> {
    if (error instanceof AuthenticationFailedException) {
      return this.obtainNewToken(returnUrl);
    }

    this.logout();
    throw error;
  }

  /**
   * Authenticates and authorizes user using a given predicate.
   *
   * @param  {AuthPredicate<UserAtProvider>} predicate predicate for authorization testing
   * @return {Observable<UserAtProvider>}    the user, if they are both authenticated and authorized
   */
  authorize(
    predicate: AuthPredicate<UserAtProvider>,
    url?: string,
  ): Observable<UserAtProvider> {
    return this.authenticate(url).pipe(
      map(user => {
        if (predicate(user)) {
          return user;
        }

        throw new AuthorizationFailedException(
          `Authorization predicate failed for user ${JSON.stringify(user)}`,
        );
      }),
      catchError(error => this.onAuthorizationFailed(error)),
    );
  }

  /**
   * Respond to an authorization failure. This is *terminal* for the user's interactions,
   * as it implies they are attempting to access a route for which they are insufficiently
   * privileged.
   *
   * This should only be called during route resolution; it is not intended for handling
   * 403 responses from APIs, as that may be recoverable on a case-by-case basis.
   *
   * @param  {any}                        error an authorization error
   * @return {Observable<UserAtProvider>}       The user, once authorized (i.e. NEVER)
   */
  onAuthorizationFailed(error: any): Observable<UserAtProvider> {
    this.logout();
    this.redirectionService.redirectToExternalUrl(
      this.authorizationFailedRedirectUrl,
    );
    return observableThrowError(error);
  }

  /**
   * Checks that the user is authorized to view a URL according to expected scopes.
   *
   * @param  {string}           url            The URL in question
   * @param  {?string[]}        requiredScopes The list of required scopes
   * @return {Observable<User>}                the user, if they are authorized
   */
  authorizeByScopes(
    url: string,
    requiredScopes: string[] = [],
  ): Observable<User> {
    const hasAnyOfRequiredScopes = (user: User) =>
      this.userScopesService.hasSomeScopeOrHigherPrivileges(
        user,
        ...requiredScopes,
      );

    return this.authorize(hasAnyOfRequiredScopes, url);
  }

  /**
   * Authorize the user for a given URL according to a list of required scopes.
   *
   * NOTE: if authentication fails, this forces the user through an IDP redirection flow.
   *
   * @param  {string}           url          The URL being accessed
   * @param  {boolean}          isTalisLogin `true` for a Talis user login; `false` otherwise
   * @return {Observable<User>}              The user, once authorized
   */
  authorizeUserType(url: string, isTalisLogin: boolean): Observable<User> {
    const requiredScopes =
      this.userScopesService.requiredScopesToAuthorize(isTalisLogin);
    return this.authorizeByScopes(url, requiredScopes);
  }

  /**
   * Authorize a user against a URL.
   *
   * @param  {string}           url The URL being authorized
   * @return {Observable<User>}     The user, if authorized
   */
  authorizeByUrl(url?: string): Observable<User> {
    const uri = url ? new URI(url) : this.getCurrentUri();
    const isTalisLogin = this.isTalisLoginUri(uri);

    return this.authorizeUserType(url, isTalisLogin);
  }

  // -- utility functions

  /**
   * Get a reference to the current page URI.
   *
   * NOTE: this is used for testing purposes.
   *
   * @return {URI} - the current page URI
   */
  getCurrentUri(): URI {
    return new URI();
  }

  /**
   * Is this URI requesting a Talis login?
   *
   * @param  {URI} uri The URI in question
   * @return {boolean}     `true` for a Talis login; `false` otherwise
   */
  isTalisLoginUri(uri: URI): boolean {
    return uri.query(true)['talis_login'] ? true : false;
  }

  /**
   * Get a bearer token for the current user, from their Persona profile.
   *
   * @param  {string} token An optional access token, for use in the Bearer.
   * @return {string}       The user's Persona bearer token.
   */
  getBearerToken(token?: string): string | undefined {
    const accessToken =
      token || (this.currentUser && this.currentUser.accessToken);
    return accessToken ? accessToken : undefined;
  }

  /**
   * Gets an 'authorization' header wrapping the bearer token, if available.
   *
   * @param  {string} token An optional access token, for use in the Bearer.
   * @return {AuthorizationHeaderJson|undefined} An 'authorization' header JSON object
   */
  getBearerHeader(accessToken?: string): AuthorizationHeaderJson | undefined {
    const bearerToken = this.getBearerToken(accessToken);
    const authorization = bearerToken ? `Bearer ${bearerToken}` : undefined;
    return authorization ? { authorization } : undefined;
  }

  /**
   * Gets an 'authorization' header wrapping the bearer token, if available.
   *
   * @return {EffectiveUserHeaderJson|undefined} An effective-user header JSON object
   */
  getUserHeader(): EffectiveUserHeaderJson | undefined {
    const { guid = undefined } = this.currentUser || {};
    return guid ? { 'X-Effective-User': guid } : undefined;
  }
}
